import { Button } from "@/components/ui/button";
import { Sparkles, ChevronRight, ChevronDown, ChevronUp, Loader2, Check, Plus, X, RefreshCw, ArrowUp, ArrowDown } from "lucide-react";
import type { ThemeSuggestion } from "@/resources/keyword-theme";
import { formatMetricNumber, NA_LABEL } from "@/utils/keywordMetricDisplay";
import { RankBadge, TrendCell } from "./KeywordThemeShared";

interface AiSuggestionsTabProps {
  isLoadingAiSuggestions: boolean;
  aiSuggestions: ThemeSuggestion[] | null;
  autoSuggest: boolean;
  isGenerating: boolean;
  acceptingIndex: number | null;
  expanded: Set<string>;
  acceptedNames: Set<string>;
  onAskAI: () => void;
  onDismiss: (index: number) => void;
  onToggleExpand: (themeName: string) => void;
  onAcceptSuggestion: (suggestion: ThemeSuggestion, index: number) => void;
  onSwitchToManual: () => void;
}

export function AiSuggestionsTab({
  isLoadingAiSuggestions,
  aiSuggestions,
  autoSuggest,
  isGenerating,
  acceptingIndex,
  expanded,
  acceptedNames,
  onAskAI,
  onDismiss,
  onToggleExpand,
  onAcceptSuggestion,
  onSwitchToManual,
}: AiSuggestionsTabProps) {
  if (isLoadingAiSuggestions) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          Signal AI is analyzing your keyword bank…
        </p>
      </div>
    );
  }

  if (aiSuggestions !== null && aiSuggestions.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Signal AI has identified{" "}
            <span className="font-semibold text-foreground">
              {aiSuggestions.length} thematic grouping {aiSuggestions.length === 1 ? "opportunity" : "opportunities"}
            </span>{" "}
            from your keyword bank.
          </p>
          <Button variant="ghost" size="sm" onClick={onAskAI} disabled={isGenerating} className="shrink-0">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>

        {aiSuggestions.map((s, i) => {
          const kws = s.keywords ?? [];
          const isExp = expanded.has(s.theme_name);
          const isAccepted = acceptedNames.has(s.theme_name);
          const nonNullRanks = kws.map((k) => k.hero_rank).filter((n): n is number => n != null);
          const avgRank = nonNullRanks.length ? Math.round(nonNullRanks.reduce((a, b) => a + b, 0) / nonNullRanks.length) : null;
          const nonNullKps = kws.map((k) => k.kps).filter((n): n is number => n != null);
          const avgKps = nonNullKps.length ? Math.round(nonNullKps.reduce((a, b) => a + b, 0) / nonNullKps.length) : null;
          const totalVol = kws.reduce((acc, k) => acc + (k.search_volume ?? 0), 0);
          const nonNullRel = kws.map((k) => k.relevance).filter((n): n is number => n != null);
          const avgRel = nonNullRel.length ? Math.round(nonNullRel.reduce((a, b) => a + b, 0) / nonNullRel.length) : null;
          const trendUp = kws.filter((k) => Number(k.trend ?? 0) > 0).length;
          const trendDown = kws.filter((k) => Number(k.trend ?? 0) < 0).length;
          return (
            <div key={i} className="border rounded-lg p-5 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1.5 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Sparkles className="h-4 w-4 text-primary shrink-0" />
                    <h4 className="text-base font-semibold text-foreground">{s.theme_name}</h4>
                    <span className="text-xs text-muted-foreground">
                      {kws.length} keyword{kws.length !== 1 ? "s" : ""}{totalVol > 0 && <> · {formatMetricNumber(totalVol)} vol/mo</>}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.rationale}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isAccepted && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDismiss(i)}
                    >
                      <X className="h-4 w-4" />
                      Dismiss
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={isAccepted ? "outline" : "default"}
                    disabled={isAccepted || acceptingIndex !== null}
                    onClick={() => onAcceptSuggestion(s, i)}
                  >
                    {acceptingIndex === i ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : isAccepted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {isAccepted ? "Added" : "Add group"}
                  </Button>
                </div>
              </div>

              <div className="rounded-sm border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground bg-muted/30">
                      <th className="text-left font-normal px-3 py-2">Keyword</th>
                      <th className="text-center font-normal px-2 py-2 w-[70px]">Rank</th>
                      <th className="text-center font-normal px-2 py-2 w-[60px]">KPS</th>
                      <th className="text-center font-normal px-2 py-2 w-[100px]">Search Vol.</th>
                      <th className="text-center font-normal px-2 py-2 w-[55px]">Rel.</th>
                      <th className="text-center font-normal px-2 py-2 w-[70px]">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-muted/50 font-semibold border-t border-border">
                      <td className="px-3 py-2 text-foreground">{kws.length} keywords</td>
                      <td className="px-2 py-2 text-center text-foreground">
                        {avgRank != null ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-[10px] text-muted-foreground uppercase">Avg</span>
                            <RankBadge rank={avgRank} />
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-2 text-center text-foreground">
                        {avgKps != null ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-[10px] text-muted-foreground uppercase">Avg</span>
                            <span>{avgKps}</span>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-2 text-center text-foreground">
                        {totalVol > 0 ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-[10px] text-muted-foreground uppercase">Agr</span>
                            <span>{formatMetricNumber(totalVol)}</span>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-2 text-center text-foreground">
                        {avgRel != null ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-[10px] text-muted-foreground uppercase">Avg</span>
                            <span>{avgRel}</span>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-2 text-center text-foreground">
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="inline-flex items-center gap-0.5 text-[hsl(var(--trend-positive))] tabular-nums">
                            <ArrowUp className="h-3 w-3" strokeWidth={2.5} />
                            {trendUp}
                          </span>
                          <span className="inline-flex items-center gap-0.5 text-destructive tabular-nums">
                            <ArrowDown className="h-3 w-3" strokeWidth={2.5} />
                            {trendDown}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExp && kws.map((kw) => (
                      <tr key={kw.keyword_id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-3 py-2 text-foreground">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{kw.keyword}</span>
                            {kw.match_type === "head_term" && (
                              <span className="inline-flex items-center rounded-full bg-status-info-soft px-2 py-0.5 text-[10px] font-medium text-foreground whitespace-nowrap">
                                Head term
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center"><RankBadge rank={kw.hero_rank} emptyLabel={NA_LABEL} /></td>
                        <td className="px-2 py-2 text-center">{kw.kps != null ? Math.round(kw.kps) : "—"}</td>
                        <td className="px-2 py-2 text-center">{kw.search_volume != null ? formatMetricNumber(kw.search_volume) : "—"}</td>
                        <td className="px-2 py-2 text-center">{kw.relevance ?? "—"}</td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center">
                            <TrendCell trend={kw.trend} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="text-primary -ml-2"
                onClick={() => onToggleExpand(s.theme_name)}
              >
                {isExp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {isExp ? "Show fewer keywords" : "View keywords"}
              </Button>
            </div>
          );
        })}
      </div>
    );
  }

  if (aiSuggestions !== null) {
    /* Tried but no results */
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
          ✦ Signal AI
        </div>
        <h3 className="text-base font-semibold">No new theme opportunities found</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Signal AI didn't find any strong unthemed keyword clusters for this product right now.
          Your keyword bank may be fully covered by existing themes, or the remaining keywords
          don't meet the quality threshold.
        </p>
        <div className="flex gap-2 mt-1">
          <Button variant="outline" onClick={onAskAI} className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Try again
          </Button>
          <Button variant="outline" onClick={onSwitchToManual}>
            Add manually
          </Button>
        </div>
      </div>
    );
  }

  /* Never tried */
  return (
    <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
        ✦ Signal AI
      </div>
      <h3 className="text-base font-semibold">Explore new thematic groupings</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {autoSuggest
          ? "Signal AI will analyze your keyword bank and suggest thematic groupings that pass the acceptance criteria."
          : "Signal AI will look for additional theme opportunities beyond your existing groupings."}
      </p>
      <Button onClick={onAskAI} className="mt-2 gap-1.5">
        <Sparkles className="h-4 w-4" />
        {autoSuggest ? "✦ Ask Signal AI" : "Ask for more suggestions"}
      </Button>
      {autoSuggest && (
        <button
          onClick={onSwitchToManual}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
        >
          Pick keywords manually
          <ChevronRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
